import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import axios from "axios";
import BASE_URL from "../API/api";
import routes from "../Components/routes/route";
import { getAuthType } from "../API/authAPI";
import { useLocation } from "react-router-dom";
import axiosInstance from "../API/axiosConfig";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // ✅ IMPORTANT: read token immediately (prevents login/signup flash)
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem("token");
    return token ? { token } : null;
  });

  const [profile, setProfile] = useState(null);
  const [loginVerify, setLoginVerify] = useState(null);
  const [portalAllData, setPortalAllData] = useState(null);

  // ✅ your existing loading
  const [isLoading, setIsLoading] = useState(true);

  const [authType, setAuthType] = useState(null);
  const [portalSettings, setPortalSettings] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [portalStatus, setPortalStatus] = useState("loading");
  const [portalErrorMsg, setPortalErrorMsg] = useState("");
  const [telegramConfig, setTelegramConfig] = useState({ link: null });

  const location = useLocation();
  const firstLoadRef = useRef(true);

  const DEFAULT_CHANNELS = { whatsapp: 0, tawk: 0, telegram: 0 };
  const [portalChannels, setPortalChannels] = useState(DEFAULT_CHANNELS);

  // ✅ Gift claim side-effect (your same code)
  useEffect(() => {
    const code = localStorage.getItem("pendingGiftCode");
    if (!user?.token || !code) return;

    (async () => {
      try {
        const upper = code;

        await axiosInstance.get("/gift-envelope/validate", {
          params: { id: upper },
        });

        const payload = { mobile: user?.mobile, code: upper };
        const claimRes = await axiosInstance.post(
          "/gift-envelope/claim",
          payload
        );

        sessionStorage.setItem(
          "giftFlash",
          JSON.stringify({
            type: "success",
            message: claimRes?.data?.message || "Gift claimed successfully 🎉",
            amount: claimRes?.data?.amount,
          })
        );
        window.dispatchEvent(new Event("giftFlash"));
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Gift link invalid, used, or claim failed.";
        sessionStorage.setItem(
          "giftFlash",
          JSON.stringify({ type: "error", message: msg })
        );
        window.dispatchEvent(new Event("giftFlash"));
      } finally {
        localStorage.removeItem("pendingGiftCode");
      }
    })();
  }, [user?.token, user?.mobile]);

  // --- Fetch user data ---
  const fetchUser = useCallback(async (token) => {
    try {
      const response = await axios.get(`${BASE_URL}/player/verify-token`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200 && response.data.type === "valid") {
        const { user: userData, portal_settings, status } = response.data;

        setUser({ token, ...userData });
        setLoginVerify(status);
        setPortalAllData(response.data);
        setProfile(userData);
        setAvatar(userData);
        setPortalSettings(portal_settings);
      } else if (response.data?.type === "player_inactive") {
        console.warn("Player is inactive. Logging out...");
        localStorage.removeItem("token");
        setUser(null);
        setAvatar(null);
      } else {
        // ✅ invalid token fallback
        localStorage.removeItem("token");
        setUser(null);
        setAvatar(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      // ✅ logout local only (avoid API logout call if token invalid)
      localStorage.removeItem("token");
      setUser(null);
      setAvatar(null);
    } finally {
      setIsLoading(false); // ✅ authLoading will become false
    }
  }, []);

  // --- Fetch auth type ---
  const fetchAuthType = async () => {
    try {
      const res = await getAuthType();
      setAuthType(res);
    } catch (error) {
      console.error("Failed to fetch auth type", error);
    }
  };

  // --- Fetch portal status ---
  const fetchPortalStatus = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/portal-info`);

      if (res?.data?.status_code === 403) {
        setPortalStatus("forbidden");
        setPortalErrorMsg(res?.data?.msg || "Access denied.");
        return;
      }

      setPortalStatus("active");

      const map = Object.fromEntries(
        (res?.data?.channels || []).map((c) => [c.channel, Number(!!c.status)])
      );

      setPortalChannels({ whatsapp: 0, tawk: 0, telegram: 0, ...map });
    } catch (error) {
      if (error.response?.status === 403) {
        setPortalStatus("forbidden");
        setPortalErrorMsg("Portal API access forbidden.");
      } else {
        console.error("Failed to fetch portal info", error);
        setPortalStatus("active");
        setPortalChannels({ whatsapp: 0, tawk: 0, telegram: 0 });
      }
    }
  };

  // WhatsAPP icon Starts
  const [waConfig, setWaConfig] = useState({ phone: null, text: null });
  const [tawkConfig, setTawkConfig] = useState({
    url: null,
    propertyId: null,
    widgetId: null,
  });

  function toWaPhone(raw) {
    if (!raw) return null;
    return String(raw).replace(/[^\d]/g, "");
  }
  function parseTawk(url) {
    try {
      const u = new URL(url);
      const parts = u.pathname.split("/").filter(Boolean);
      const widgetId = parts[parts.length - 1] || null;
      const propertyId = parts[parts.length - 2] || null;
      return { propertyId, widgetId };
    } catch {
      return { propertyId: null, widgetId: null };
    }
  }

  const fetchCommunication = async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/communication`);

      const map = Object.fromEntries(
        (data?.channels || []).map((c) => [c.channel, Number(!!c.status)])
      );
      setPortalChannels({ whatsapp: 0, tawk: 0, telegram: 0, ...map });

      let w = { phone: null, text: null };
      let t = { url: null, propertyId: null, widgetId: null };
      let tg = { link: null };

      for (const c of data?.channels || []) {
        if (c.channel === "whatsapp" && c.status) {
          w = {
            phone: toWaPhone(c?.config?.phone),
            text: c?.config?.text || "Hi! How can we help you?",
          };
        }

        if (c.channel === "tawk" && c.status) {
          const url = c?.config?.tawk_url || null;
          const ids = url ? parseTawk(url) : { propertyId: null, widgetId: null };
          t = { url, ...ids };
        }

        if (c.channel === "telegram" && c.status) {
          tg = { link: c?.config?.link || null };
        }
      }

      setWaConfig(w);
      setTawkConfig(t);
      setTelegramConfig(tg);
    } catch (e) {
      console.error("communication fetch failed", e);
      setWaConfig({ phone: null, text: null });
      setTawkConfig({ url: null, propertyId: null, widgetId: null });
      setTelegramConfig({ link: null });
    }
  };

  // --- First load init ---
  useEffect(() => {
    const initApp = async () => {
      setIsLoading(true); // ✅ ensure loading true on refresh

      await fetchPortalStatus();
      await fetchCommunication();

      const token = localStorage.getItem("token");
      if (token) {
        // ✅ keep token user immediately so UI won't show login buttons
        setUser((prev) => ({ ...(prev || {}), token }));
        await fetchUser(token);
      } else {
        setIsLoading(false);
      }

      firstLoadRef.current = false;
    };

    initApp();
  }, [fetchUser]);

  // --- Login ---
  const login = (token) => {
    localStorage.setItem("token", token);

    // ✅ set immediately (prevents flash after login too)
    setUser({ token });

    setIsLoading(true);
    fetchUser(token);
  };

  // --- Logout ---
  const logout = async (navigate) => {
    try {
      const token = user?.token;
      if (token) {
        await axios.post(
          `${BASE_URL}/player/logout`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      localStorage.removeItem("token");
      setUser(null);
      setAvatar(null);
      setProfile(null);
      setPortalSettings(null);
      if (navigate) navigate(routes.home);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,

        // ✅ keep existing
        isLoading,

        // ✅ ADD THIS (your header uses authLoading)
        authLoading: isLoading,

        authType,
        portalSettings,
        avatar,
        setAvatar,
        setProfile,
        login,
        logout,
        fetchAuthType,
        fetchUser,
        portalStatus,
        portalErrorMsg,
        loginVerify,
        portalAllData,
        portalChannels,
        waConfig,
        tawkConfig,
        telegramConfig,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
